import { useQuery } from "@apollo/client";
import {
  CheckCircle,
  ChevronRight,
  CircleNotifications,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import {
  CampaignStatus,
  EnqueueTexterSendMutation,
  EnqueueTexterSendMutationVariables,
  ListMyInboxesQuery,
  ListMyInboxesQueryVariables,
  ReleaseAssignmentsMutation,
  ReleaseAssignmentsMutationVariables,
  RequestAssignmentBatchMutation,
  RequestAssignmentBatchMutationVariables,
  TexterCampaignSendStatus,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import Table from "~frontend/components/Table";
import {
  RELEASE_ASSIGNMENTS,
  REQUEST_ASSIGNMENT_BATCH,
} from "~frontend/graphql/assignments";
import { ENQUEUE_TEXTER_SEND } from "~frontend/graphql/campaigns";
import { LIST_MY_INBOXES } from "~frontend/graphql/inboxes";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { formatDateTime } from "~frontend/util/dateFormat";
import useMutation from "~frontend/util/useMutation";

import { InboxCampaignSummary } from "./InboxCampaignSummary";
import { ReplyControls } from "./ReplyControls";
import { CampaignInboxSummary } from "./types";

export function InboxList(): ReactElement {
  const snackbar = useSnackbar();
  const intl = useIntl();
  const { subaccount, subaccountLink } = useSubaccount()!;
  const navigate = useNavigate();

  useBreadcrumbs(
    [
      {
        label: subaccount.name,
        path: subaccountLink("/inboxes"),
        backArrow: false,
      },
    ],
    { hideSubaccountTitle: true }
  );

  const goToInbox = useCallback(
    ({ inboxId }: { inboxId: string }) =>
      navigate(subaccountLink(`/inboxes/${inboxId}`)),
    [navigate, subaccountLink]
  );

  const { loading, error, data, startPolling, stopPolling } = useQuery<
    ListMyInboxesQuery,
    ListMyInboxesQueryVariables
  >(LIST_MY_INBOXES, {
    variables: {
      input: { subaccountId: subaccount.id },
    },
  });

  const [inboxesUpdatedAt, setInboxesUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (data) {
      setInboxesUpdatedAt(new Date());
    }
  }, [data, setInboxesUpdatedAt]);

  useEffect(() => {
    const hasPendingCampaignSend = data?.listMyInboxes
      .flatMap(inbox =>
        inbox.__typename === "CampaignInboxSummary"
          ? inbox.campaign.myCampaignSends
          : []
      )
      .some(send => send.status === TexterCampaignSendStatus.Pending);

    if (hasPendingCampaignSend) {
      startPolling(1000);
    } else {
      startPolling(60 * 1000);
    }

    return () => stopPolling();
  }, [data, startPolling, stopPolling]);

  const [requestAssignmentBatch, { loading: requestAssignmentBatchLoading }] =
    useMutation<
      RequestAssignmentBatchMutation,
      RequestAssignmentBatchMutationVariables
    >(REQUEST_ASSIGNMENT_BATCH, {
      refetchQueries: [LIST_MY_INBOXES],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: intl.formatMessage({
        defaultMessage: "Error claiming replies",
        description: "Error message for claiming replies",
      }),
    });

  const [releaseAssignments, { loading: releaseAssignmentsLoading }] =
    useMutation<
      ReleaseAssignmentsMutation,
      ReleaseAssignmentsMutationVariables
    >(RELEASE_ASSIGNMENTS, {
      refetchQueries: [LIST_MY_INBOXES],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: intl.formatMessage({
        defaultMessage: "Error releasing replies",
        description: "Error message for releasing replies",
      }),
    });

  const assignmentRequestInProgress =
    requestAssignmentBatchLoading || releaseAssignmentsLoading;

  const handleRequestAssignmentBatch = useCallback(
    async ({ inboxId }: { inboxId: string }) => {
      if (assignmentRequestInProgress) {
        return;
      }

      const requestAssignmentBatchResponse = await requestAssignmentBatch({
        variables: { input: { inboxId } },
      });

      if (
        requestAssignmentBatchResponse.errors ||
        !requestAssignmentBatchResponse.data
      ) {
        console.error(requestAssignmentBatchResponse.errors);
        snackbar.enqueueSnackbar(
          intl.formatMessage({
            defaultMessage: "Error claiming replies",
            description: "Error message for claiming replies",
          })
        );
        return;
      }

      goToInbox({ inboxId })?.catch(console.error);
    },
    [
      assignmentRequestInProgress,
      requestAssignmentBatch,
      snackbar,
      intl,
      goToInbox,
    ]
  );

  const handleReleaseAssignments = useCallback(
    async ({ inboxId }: { inboxId?: string } = {}) => {
      if (assignmentRequestInProgress) {
        return;
      }

      const releaseAssignmentsResponse = await releaseAssignments({
        variables: { input: { subaccountId: subaccount.id, inboxId } },
      });

      if (
        releaseAssignmentsResponse.errors ||
        !releaseAssignmentsResponse.data
      ) {
        console.error(releaseAssignmentsResponse.errors);
        snackbar.enqueueSnackbar(
          intl.formatMessage({
            defaultMessage: "Error releasing replies",
            description: "Error message for releasing replies",
          })
        );
        return;
      }
    },
    [
      assignmentRequestInProgress,
      releaseAssignments,
      subaccount,
      snackbar,
      intl,
    ]
  );

  const [
    enqueuingTexterSendForCampaignId,
    setEnqueuingTexterSendForCampaignId,
  ] = useState<string | null>(null);

  const [enqueueTexterSend] = useMutation<
    EnqueueTexterSendMutation,
    EnqueueTexterSendMutationVariables
  >(ENQUEUE_TEXTER_SEND, {
    refetchQueries: [LIST_MY_INBOXES],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Error sending initials",
      description: "Error message for sending initial messages",
    }),
  });

  const handleEnqueueTexterSend = useCallback(
    async (campaignId: string) => {
      if (enqueuingTexterSendForCampaignId) {
        return;
      }

      setEnqueuingTexterSendForCampaignId(campaignId);

      try {
        const { data, errors } = await enqueueTexterSend({
          variables: { input: { campaignId } },
        });

        if (errors || !data) {
          snackbar.enqueueSnackbar(
            intl.formatMessage({
              defaultMessage: "Error sending initials",
              description: "Error message for sending initial messages",
            })
          );
        }
      } finally {
        setEnqueuingTexterSendForCampaignId(null);
      }
    },
    [
      enqueuingTexterSendForCampaignId,
      setEnqueuingTexterSendForCampaignId,
      enqueueTexterSend,
      snackbar,
      intl,
    ]
  );

  if (loading) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  const inboxes = data!.listMyInboxes;
  const escalationInboxes = inboxes.filter(
    inbox => inbox.__typename === "EscalationInboxSummary"
  );
  const generalInboxes = inboxes.filter(
    inbox => inbox.__typename === "GeneralInboxSummary"
  );
  const campaignInboxes: CampaignInboxSummary[] = inboxes
    .filter(inbox => inbox.__typename === "CampaignInboxSummary")
    .filter(inbox => inbox.campaign.status !== CampaignStatus.Archived);

  const totalCampaignAssignedReplies = _.sumBy(
    campaignInboxes,
    inbox => inbox.stats.numRepliesAssigned
  );

  const canSendInitials = (inbox: CampaignInboxSummary) =>
    inbox.campaign.texterSendEnabled &&
    inbox.campaign.withinTextingHours &&
    inbox.campaign.status === CampaignStatus.InitialSendRunning;

  return (
    <PageContainer maxWidth="md">
      <FlexBox direction="column" gap="24px">
        {escalationInboxes.map(inbox => (
          <InboxListSection
            key={`inbox-${inbox.id}`}
            title={inbox.inboxName}
            label={intl.formatMessage({
              defaultMessage: "Escalation",
              description: "Label for an escalation inbox",
            })}
            alerts={[
              {
                key: "available-escalations",
                text: intl.formatMessage(
                  {
                    defaultMessage:
                      "{numRepliesAvailable, plural, =1 {1 Available Escalation} other {{numRepliesAvailable} Available Escalations}}",
                    description:
                      "Alert for the number of available escalations in an inbox",
                  },
                  { numRepliesAvailable: inbox.stats.numRepliesAvailable }
                ),
              },
              {
                key: "your-escalationns",
                text: intl.formatMessage(
                  {
                    defaultMessage:
                      "{numAssignments, plural, =1 {1 Your Assignment} other {{numAssignments} Your Escalations}}",
                    description:
                      "Alert for the number of escalations assigned to the user",
                  },
                  { numAssignments: inbox.stats.numAssignments }
                ),
                icon:
                  inbox.stats.numAssignments > 0 ? (
                    <CircleNotifications color="error" sx={{ width: "18px" }} />
                  ) : (
                    <CheckCircle color="success" sx={{ width: "18px" }} />
                  ),
              },
            ]}
            goButton={{ linkHref: subaccountLink(`/inboxes/${inbox.id}`) }}
          />
        ))}
        {generalInboxes.map(inbox => (
          <InboxListSection
            key={`inbox-${inbox.id}`}
            title={inbox.inboxName}
            alerts={[
              ...(inbox.availableTexterSend
                ? [
                    {
                      key: "available-texter-sends",
                      text: intl.formatMessage({
                        defaultMessage: "Texter sends available",
                        description: "Alert for texter sends available",
                      }),
                    },
                  ]
                : []),
              {
                key: "available-replies",
                text: intl.formatMessage(
                  {
                    defaultMessage:
                      "{numRepliesAvailable, plural, =1 {1 Available Reply} other {{numRepliesAvailable} Available Replies}}",
                    description:
                      "Alert for the number of available replies in an inbox",
                  },
                  { numRepliesAvailable: inbox.stats.numRepliesAvailable }
                ),
              },
              {
                key: "your-replies",
                text: intl.formatMessage(
                  {
                    defaultMessage:
                      "{numAssignments, plural, =1 {1 Your Reply} other {{numAssignments} Your Replies}}",
                    description:
                      "Alert for the number of replies assigned to the user",
                  },
                  { numAssignments: inbox.stats.numAssignments }
                ),
                icon:
                  inbox.stats.numAssignments > 0 ? (
                    <CircleNotifications color="error" sx={{ width: "18px" }} />
                  ) : (
                    <CheckCircle color="success" sx={{ width: "18px" }} />
                  ),
              },
            ]}
            goButton={{ linkHref: subaccountLink(`/inboxes/${inbox.id}`) }}
          />
        ))}
        <InboxListSection
          title={intl.formatMessage({
            defaultMessage: "Your Campaigns",
            description: "Header for campaign inbox list",
          })}
          updatedAt={inboxesUpdatedAt ?? undefined}
          alerts={
            campaignInboxes.length > 0
              ? [
                  {
                    key: "campaigns",
                    text: intl.formatMessage(
                      {
                        defaultMessage:
                          "{numCampaigns, plural, =1 {1 Campaign} other {{numCampaigns} Campaigns}}",
                        description: "Alert for the number of campaigns",
                      },
                      { numCampaigns: campaignInboxes.length }
                    ),
                  },
                  {
                    key: "replies",
                    text: intl.formatMessage(
                      {
                        defaultMessage:
                          "{numReplies, plural, =1 {1 Reply} other {{numReplies} Replies}}",
                        description: "Alert for the number of replies",
                      },
                      { numReplies: totalCampaignAssignedReplies }
                    ),
                    icon:
                      totalCampaignAssignedReplies > 0 ? (
                        <CircleNotifications
                          color="error"
                          sx={{ width: "18px" }}
                        />
                      ) : (
                        <CheckCircle color="success" sx={{ width: "18px" }} />
                      ),
                  },
                ]
              : []
          }
        >
          <Table
            emptyState={
              <FormattedMessage
                defaultMessage="You aren't assigned to any campaigns."
                description="Empty state message for a texter with no campaigns"
              />
            }
            rows={campaignInboxes}
            columns={[
              {
                key: "campaign",
                title: intl.formatMessage({
                  defaultMessage: "Campaign",
                  description: "Header for campaign column",
                }),
                renderValue: inbox => {
                  return (
                    <InboxCampaignSummary
                      campaign={inbox.campaign}
                      showTexterSendInfo={true}
                    />
                  );
                },
              },
              {
                key: "availableReplies",
                title: intl.formatMessage({
                  defaultMessage: "Available Replies",
                  description: "Header for available replies column",
                }),
                renderValue: inbox => (
                  <Typography>
                    {inbox.stats.numRepliesAvailable.toLocaleString()}
                  </Typography>
                ),
              },
              {
                key: "yourReplies",
                title: intl.formatMessage({
                  defaultMessage: "Your Replies",
                  description: "Header for your replies column",
                }),
                renderValue: inbox => (
                  <Typography>
                    {inbox.stats.numRepliesAssigned.toLocaleString()}
                  </Typography>
                ),
              },
              {
                key: "actions",
                renderValue: inbox => (
                  <ReplyControls
                    assignmentCount={inbox.stats.numAssignments}
                    availableCount={inbox.stats.numRepliesAvailable}
                    assignmentNeedsReplyCount={inbox.stats.numRepliesAssigned}
                    canSendInitials={canSendInitials(inbox) ?? false}
                    texterSendInProgress={
                      enqueuingTexterSendForCampaignId === inbox.campaign.id ||
                      inbox.campaign.myCampaignSends.some(
                        send => send.status === TexterCampaignSendStatus.Pending
                      )
                    }
                    goToCampaign={() => goToInbox({ inboxId: inbox.id })}
                    texterSendBatchSize={
                      inbox.campaign.texterSendBatchSize ?? null
                    }
                    handleEnqueueTexterSend={() =>
                      handleEnqueueTexterSend(inbox.campaign.id)
                    }
                    handleReleaseAssignments={() =>
                      handleReleaseAssignments({ inboxId: inbox.id })
                    }
                    handleRequestAssignmentBatch={() =>
                      handleRequestAssignmentBatch({ inboxId: inbox.id })
                    }
                  />
                ),
              },
            ]}
            noFooter
          />
          <FlexBox justifyContent="flex-end" sx={{ marginTop: "8px" }}>
            <LinkButton onClick={() => handleReleaseAssignments()}>
              <Typography variant="body3">
                <FormattedMessage
                  defaultMessage="Release All My Replies"
                  description="Button text for releasing all replies"
                />
              </Typography>
            </LinkButton>
          </FlexBox>
        </InboxListSection>
      </FlexBox>
    </PageContainer>
  );
}

function InboxListSection({
  title,
  updatedAt,
  label,
  alerts,
  goButton,
  children,
}: {
  title: string;
  updatedAt?: Date;
  label?: string;
  alerts?: { key: string; text: ReactNode; icon?: ReactNode }[];
  goButton?: { linkHref: string };
  children?: ReactNode;
}): ReactElement {
  return (
    <Box
      sx={{
        backgroundColor: palette.white,
        boxShadow: `0px 1px 5px 0px #6A678333`,
        paddingTop: label ? "18px" : "32px",
        paddingBottom: "32px",
        paddingLeft: "80px",
        paddingRight: goButton ? "20px" : "80px",
      }}
    >
      {label && (
        <Chip
          sx={{
            height: "15px",
            borderRadius: 0,
            backgroundColor: palette.lightestOrange,
            margin: 0,
            "& .MuiChip-label": { padding: "0 4px" },
          }}
          label={<Typography variant="h4Caps">{label}</Typography>}
        />
      )}
      <FlexBox justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h0Bold" component="div">
            {title}
          </Typography>
          {updatedAt && (
            <Typography variant="body3">
              <FormattedMessage
                defaultMessage="Last updated: {updatedAt}"
                description="Last updated text"
                values={{
                  updatedAt: formatDateTime(updatedAt.getTime()),
                }}
              />
            </Typography>
          )}
        </Box>
        <FlexBox alignItems="center" gap="20px">
          <FlexBox alignItems="center" gap="24px">
            {alerts?.map(alert => (
              <FlexBox key={alert.key} alignItems="center" gap="6px">
                <Typography variant="body1Bold">{alert.text}</Typography>
                {alert.icon}
              </FlexBox>
            ))}
          </FlexBox>
          {goButton && (
            <IconButton href={goButton.linkHref}>
              <ChevronRight />
            </IconButton>
          )}
        </FlexBox>
      </FlexBox>
      {children}
    </Box>
  );
}
